import { Grid, GridItem } from '@chakra-ui/react';
import {
  ChakraAutocompleteField,
  ChakraFormSwitch,
  InputField,
} from 'app/components/Core';
import { DurationTextField } from 'app/components/Song/Details/SongDetailsForm/DurationTextField';
import { TimeSignatureInput } from 'app/components/Song/Details/TimeSignatureInput';
import { Controller } from 'react-hook-form';
import { useLabelV2VersionDetails } from './hooks';

export const LabelV2VersionDetailsForm = ({
  form: {
    register,
    control,
    formState: { errors },
  },
  onSubmit,
  fallbackAutocompleteFieldToSnapshot,
  fallbackTextFieldToSnapshot,
  genres,
  onGenreChange,
  keySignatures,
  languages,
  getErrorMessage,
}: ReturnType<typeof useLabelV2VersionDetails>) => {
  const artistError = getErrorMessage('artist');
  const genreError = getErrorMessage('genre');

  return (
    <form onSubmit={onSubmit}>
      <Grid templateColumns="repeat(4, 1fr)" gap={4} maxW="640" m="auto">
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <ChakraAutocompleteField
            control={control}
            name="genre"
            label="Genre"
            options={genres}
            onChange={onGenreChange}
            isDisabled
            isInvalid={genreError?.invalid}
            errorMessage={genreError?.element}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <ChakraAutocompleteField
            control={control}
            name="language"
            label="Language"
            options={languages}
            isClearable
            isDisabled
            onBlur={fallbackAutocompleteFieldToSnapshot('language', languages)}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }} h={70}>
          <ChakraFormSwitch
            control={control}
            label="Explicit Lyrics"
            name="explicit"
            isDisabled
            onChange={onSubmit}
            formControlProps={{ cursor: 'not-allowed' }}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <InputField
            {...register('displayArtistName')}
            label="Artist"
            isDisabled
            onBlur={fallbackTextFieldToSnapshot('displayArtistName')}
            isInvalid={artistError?.invalid}
            errorMessage={artistError?.element}
            _hover={{}}
            style={{ cursor: 'not-allowed' }}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <ChakraAutocompleteField
            control={control}
            name="keySignature"
            label="Key Signature"
            options={keySignatures}
            isClearable
            isDisabled
            onBlur={fallbackAutocompleteFieldToSnapshot(
              'keySignature',
              keySignatures
            )}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <TimeSignatureInput
            control={control}
            name="timeSignature"
            label="Time Signature"
            disabled
            onChange={onSubmit}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <InputField
            {...register('tempo')}
            label="Tempo"
            isDisabled
            onBlur={fallbackTextFieldToSnapshot('tempo')}
            isInvalid={Boolean(errors?.tempo)}
            errorMessage={errors?.tempo?.message}
            _hover={{}}
            style={{ cursor: 'not-allowed' }}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <Controller
            name="duration"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DurationTextField
                value={value}
                onChange={onChange}
                error={error?.message}
                onBlur={fallbackTextFieldToSnapshot('duration')}
                disabled
                sx={{ _hover: {}, cursor: 'not-allowed !Important' }}
              />
            )}
          />
        </GridItem>
        <GridItem colSpan={{ base: 4, md: 2 }}>
          <InputField
            {...register('location')}
            label="Studio Location (main)"
            isDisabled
            onBlur={fallbackTextFieldToSnapshot('location')}
            isInvalid={Boolean(errors?.location)}
            errorMessage={errors?.location?.message}
            _hover={{}}
            style={{ cursor: 'not-allowed' }}
          />
        </GridItem>
      </Grid>
    </form>
  );
};
